import { MaterialCommunityIcons } from "@expo/vector-icons";
import { formatInTz } from "@taskhealth/shared";
import { View, Text, StyleSheet } from "react-native";

import type { FormDefinition } from "@/lib/api";
import { colors, fonts, spacing, radius, shadows, visitTypeColors } from "@/lib/theme";

interface TimeClockPaymentProps {
    visit: FormDefinition["visit"];
    visitTypeName?: string;
    agencyName?: string | null;
}

function formatTime(isoString: string | null, timezone: string): string {
    if (!isoString) {
        return "--";
    }
    try {
        const date = new Date(isoString);
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: timezone
        });
    } catch {
        return "--";
    }
}

// `visits.startTime` / `endTime` are `timestamp` (no tz) — API serializes them
// with a phantom `Z`, so the UTC digits ARE the visit's local wall-clock.
// Format in "UTC" so the rendered time matches the scheduled value regardless
// of device timezone.
function formatWallClockTime(isoString: string | null): string {
    if (!isoString) {
        return "--";
    }
    try {
        return formatInTz(new Date(isoString), "UTC", "h:mm a");
    } catch {
        return "--";
    }
}

function formatDuration(startIso: string | null, endIso: string | null): string {
    if (!startIso || !endIso) {
        return "--";
    }
    const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime();
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 60) {
        return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function formatScheduledDuration(startIso: string | null, endIso: string | null): string {
    if (!startIso || !endIso) {
        return "--";
    }
    const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime();
    const hours = Math.round(diffMs / 3600000);
    return hours === 1 ? "1 Hour" : `${hours} Hours`;
}

function formatVisitDate(
    startTime: string | null,
    scheduledStart: string | null,
    timezone: string
): string {
    // Prefer `startTime` (post-assignment, naive wall-clock) and render its UTC
    // digits as-is. Fall back to `scheduledStart` (real timestamptz) converted
    // to the visit's timezone.
    if (startTime) {
        try {
            return formatInTz(new Date(startTime), "UTC", "MMMM d, yyyy");
        } catch {
            // fall through
        }
    }
    if (scheduledStart) {
        try {
            return formatInTz(new Date(scheduledStart), timezone, "MMMM d, yyyy");
        } catch {
            // fall through
        }
    }
    return "--";
}

function formatPayment(cents: number): string {
    if (cents === 0) {
        return "Pending";
    }
    return `$${(cents / 100).toFixed(2)}`;
}

export function TimeClockPayment({ visit, visitTypeName, agencyName }: TimeClockPaymentProps) {
    const tz = visit.timezone ?? "America/New_York";
    const hasClockIn = !!visit.clockInAt;
    const hasClockOut = !!visit.clockOutAt;

    return (
        <View style={styles.card}>
            {/* Agency — surfaced prominently at the top of the clock-in card so the
                RN can see at a glance which agency this visit is for. */}
            {agencyName ? (
                <View style={styles.agencyHeader}>
                    <View style={styles.agencyIconCircle}>
                        <MaterialCommunityIcons
                            name="office-building"
                            size={20}
                            color={colors.teal600}
                        />
                    </View>
                    <View style={styles.agencyTextBlock}>
                        <Text style={styles.agencyLabel}>AGENCY</Text>
                        <Text style={styles.agencyName} numberOfLines={2}>
                            {agencyName}
                        </Text>
                    </View>
                </View>
            ) : null}

            <Text style={styles.sectionLabel}>SCHEDULE & TIME</Text>

            <Text style={styles.dateText}>
                {formatVisitDate(visit.startTime, visit.scheduledStart, tz)}
            </Text>

            {/* Start Time / Clock In row */}
            <View style={styles.row}>
                <View style={styles.column}>
                    <Text style={styles.label}>START TIME</Text>
                    <Text style={styles.value}>{formatWallClockTime(visit.startTime)}</Text>
                </View>
                <View style={styles.column}>
                    <Text style={styles.label}>CLOCK IN</Text>
                    <Text style={[styles.value, hasClockIn && styles.clockedValue]}>
                        {hasClockIn ? `${formatTime(visit.clockInAt, tz)} ✓` : "--"}
                    </Text>
                </View>
            </View>

            {/* End Time / Clock Out row */}
            <View style={styles.row}>
                <View style={styles.column}>
                    <Text style={styles.label}>END TIME</Text>
                    <Text style={styles.value}>{formatWallClockTime(visit.endTime)}</Text>
                </View>
                <View style={styles.column}>
                    <Text style={styles.label}>CLOCK OUT</Text>
                    <Text style={[styles.value, hasClockOut && styles.clockedValue]}>
                        {hasClockOut ? `${formatTime(visit.clockOutAt, tz)} ✓` : "--"}
                    </Text>
                    {visit.clockOutOriginalAt ? (
                        // Spec D27: her clock-out was moved back to when her
                        // phone shows she left the home.
                        <Text style={styles.correctionNote}>
                            When you left. You clocked out at{" "}
                            {formatTime(visit.clockOutOriginalAt, tz)}.
                        </Text>
                    ) : null}
                </View>
            </View>

            {/* Duration row */}
            <View style={styles.row}>
                <View style={styles.column}>
                    <Text style={styles.label}>DURATION</Text>
                    <Text style={styles.value}>
                        {formatScheduledDuration(visit.startTime, visit.endTime)}
                    </Text>
                </View>
                {hasClockIn && hasClockOut ? (
                    <View style={styles.column}>
                        <Text style={styles.label}>ACTUAL</Text>
                        <Text style={[styles.value, styles.clockedValue]}>
                            {formatDuration(visit.clockInAt, visit.clockOutAt)}
                        </Text>
                    </View>
                ) : null}
            </View>

            {/* Payment + Visit Type */}
            <View style={styles.paymentRow}>
                <View style={styles.paymentLeft}>
                    <Text style={styles.paymentLabel}>Payment:</Text>
                    <Text style={styles.paymentValue}>{formatPayment(visit.payInCents)}</Text>
                </View>
                {visitTypeName ? (
                    <View
                        style={[
                            styles.visitTypeBadge,
                            {
                                backgroundColor: (
                                    visitTypeColors[
                                        visitTypeName as keyof typeof visitTypeColors
                                    ] ?? { bg: colors.teal50 }
                                ).bg
                            }
                        ]}
                    >
                        <Text
                            style={[
                                styles.visitTypeBadgeText,
                                {
                                    color: (
                                        visitTypeColors[
                                            visitTypeName as keyof typeof visitTypeColors
                                        ] ?? { text: colors.teal500 }
                                    ).text
                                }
                            ]}
                        >
                            {visitTypeName}
                        </Text>
                    </View>
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.md
    },
    dateText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textPrimary,
        marginBottom: spacing.md
    },
    row: {
        flexDirection: "row",
        marginBottom: spacing.sm
    },
    column: {
        flex: 1
    },
    correctionNote: {
        fontFamily: fonts.regular,
        fontSize: 11,
        color: colors.textTertiary,
        marginTop: 2
    },
    label: {
        fontFamily: fonts.bold,
        fontSize: 9,
        color: colors.textTertiary,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        marginBottom: 2
    },
    value: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.textPrimary
    },
    clockedValue: {
        color: colors.teal500
    },
    paymentRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    paymentLeft: {
        flexDirection: "row",
        alignItems: "center"
    },
    paymentLabel: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textSecondary,
        marginRight: spacing.sm
    },
    paymentValue: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.textPrimary
    },
    visitTypeBadge: {
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        borderRadius: radius.full
    },
    visitTypeBadgeText: {
        fontFamily: fonts.bold,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    agencyHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingBottom: spacing.md,
        marginBottom: spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    agencyIconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    agencyTextBlock: {
        flex: 1
    },
    agencyLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal600,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: 2
    },
    agencyName: {
        fontFamily: fonts.bold,
        fontSize: 17,
        color: colors.textPrimary,
        lineHeight: 22
    }
});
